/**
 * Outbox-Service.
 *
 * Eingangs-API für Mutations: `enqueue(kind, entityId, payload, baseLastModified?)`
 * legt einen Eintrag an. Die Sync-Engine ruft `pending()` und `markSent`/`markFailed`/
 * `markConflict` auf. `pending()` liefert nur Einträge OHNE aktiven Konflikt.
 */

import { db, type ConflictInfo, type OutboxEntry, type OutboxKind } from './schema';
import { useAuth } from '../store/auth';

export async function enqueue(
  kind: OutboxKind,
  entityId: string,
  payload?: unknown,
  baseLastModified?: string,
): Promise<number> {
  const { user, activeCompanyId } = useAuth.getState();
  const id = await db.outbox.add({
    kind,
    entityId,
    payload,
    baseLastModified,
    createdAt: Date.now(),
    attempts: 0,
    userId: user?.id,
    companyId: activeCompanyId,
  });
  // Dexie liefert die ID des neuen Eintrags zurück.
  return Number(id);
}

/** Pending-Outbox in chronologischer Reihenfolge — OHNE Konflikt-Einträge. */
export async function pending(): Promise<OutboxEntry[]> {
  return db.outbox
    .orderBy('createdAt')
    .filter((e) => !e.conflict)
    .toArray();
}

/** Anzahl der nicht-versandten Einträge insgesamt — Konflikte zählen mit. */
export async function pendingCount(): Promise<number> {
  return db.outbox.count();
}

/** Anzahl der Einträge, die in einem Konflikt feststecken. */
export async function conflictCount(): Promise<number> {
  return db.outbox.filter((e) => Boolean(e.conflict)).count();
}

/** Alle Outbox-Einträge mit aktivem Konflikt. */
export async function conflicts(): Promise<OutboxEntry[]> {
  return db.outbox.filter((e) => Boolean(e.conflict)).toArray();
}

/** Eintrag als Konflikt markieren — er wird beim nächsten Sync nicht erneut versucht. */
export async function markConflict(
  entryId: number,
  conflict: ConflictInfo,
): Promise<void> {
  await db.outbox.update(entryId, { conflict });
}

/** Konflikt-Markierung entfernen + ggf. Payload aktualisieren — Eintrag wird wieder gesynct. */
export async function clearConflict(
  entryId: number,
  patch: Partial<Pick<OutboxEntry, 'payload' | 'baseLastModified'>> = {},
): Promise<void> {
  // Dexie kann `conflict: undefined` nicht löschen, also über delete + put.
  const entry = await db.outbox.get(entryId);
  if (!entry) return;
  const updated: OutboxEntry = {
    ...entry,
    ...patch,
    attempts: 0,
    lastError: undefined,
    conflict: undefined,
  };
  await db.outbox.put(updated);
}

/** Eintrag wurde erfolgreich gesendet → entfernen. */
export async function remove(entryId: number): Promise<void> {
  await db.outbox.delete(entryId);
}

/** Eintrag schlug fehl → Versuchszähler erhöhen, Fehler speichern. */
export async function markFailed(entryId: number, error: string): Promise<void> {
  const entry = await db.outbox.get(entryId);
  await db.outbox.update(entryId, {
    attempts: (entry?.attempts ?? 0) + 1,
    lastError: error,
    lastAttemptAt: Date.now(),
  });
}

/**
 * Gesamtprüfung 11.09.2026: nach erfolgreichem Anlegen einer offline
 * erstellten Schicht/eines Eintrags die temporäre ID in allen noch
 * wartenden Einträgen durch die Server-ID ersetzen — sonst liefen spätere
 * Änderungen/Löschungen (z. B. Ausstempeln nach Offline-Einstempeln) ins
 * Leere (404) und gingen verloren.
 */
export async function rewriteEntityId(fromId: string, toId: string): Promise<void> {
  if (fromId === toId) return;
  await db.outbox.where('entityId').equals(fromId).modify({ entityId: toId });
}

/** Komplett leeren — z.B. bei Logout. */
export async function clear(): Promise<void> {
  await db.outbox.clear();
}
